import { ApiResponseModel } from '../types/ApiResponseModel';
import { AxiosProgressEvent } from '../types/AxiosProgressEvent';
import { DataRequestConfig } from '../types/DataRequestConfig';
import { LiteracyScreeningContactModel } from '../types/LiteracyScreeningContactModel';
import { LiteracyScreeningDiagnosticMeasuresModel } from '../types/LiteracyScreeningDiagnosticMeasuresModel';
import { LiteracyScreeningFileListItemModel } from '../types/LiteracyScreeningFileListItemModel';
import { LiteracyScreeningLEASchoolModel } from '../types/LiteracyScreeningLEASchoolModel';
import { LiteracyScreeningModel } from '../types/LiteracyScreeningModel';
import { OptionSetsPageModel } from '../types/OptionSetsPageModel';
import { SelectOption } from '../types/SelectOption';
import { getApiBaseUrl } from './app.service';
import { executeApiRequest, getAuthToken } from './base.service';
import { getHttpClient, HttpClient } from './httpClient';

export const literacyScreeningApiPaths = {
  getContact: 'literacyscreeningcontact',
  createContact: 'literacyscreeningcontact',
  updateContact: 'literacyscreeningcontact',
  getSchoolsBySchoolYear: (schoolYear: string) =>
    `literacyscreening/${encodeURIComponent(schoolYear)}`,
  getSchoolYears:
    'common/schoolyearsbymodule/formview/LiteracyScreening?showLongYears=true',
  getOptionSets: (module: string, page: string, include?: string) => {
    const path = `common/optionsets/${encodeURIComponent(
      module
    )}/${encodeURIComponent(page)}`;
    return include ? `${path}?include=${encodeURIComponent(include)}` : path;
  },
  getDiagnostics: (schoolYear: string) =>
    `literacyscreening/diagnostics/${encodeURIComponent(schoolYear)}`,
  createDiagnostics: 'literacyscreening/diagnostics',
  updateDiagnostics: (id: string) =>
    `literacyscreening/diagnostics/${encodeURIComponent(id)}`,
  createScreening: 'literacyscreening/screening',
  updateScreening: (id: string) =>
    `literacyscreening/screening/${encodeURIComponent(id)}`,
  getDataCollectionChecks: (schoolYear: string) =>
    `literacyscreening/datacollectionchecks/${encodeURIComponent(schoolYear)}`,
  updateDocument: (id: string) =>
    `literacyscreening/document/${encodeURIComponent(id)}`,
  uploadFile: (
    schoolYear: string,
    schoolIdentifier: string,
    leaIdentifier: string,
    documentType: string
  ): string =>
    `file/literacyscreening?schoolYear=${encodeURIComponent(
      schoolYear
    )}&schoolIdentifier=${encodeURIComponent(
      schoolIdentifier
    )}&leaIdentifier=${encodeURIComponent(
      leaIdentifier
    )}&documentType=${encodeURIComponent(documentType)}`,
  getFileById: (leaId: string, id: string): string =>
    `file/literacyscreening/${encodeURIComponent(leaId)}/${encodeURIComponent(
      id
    )}`,
  deleteFile: (leaId: string, id: string): string =>
    `file/literacyscreening/${encodeURIComponent(leaId)}/${encodeURIComponent(
      id
    )}`,
  getFilesByLea: (leaId: string): string =>
    `file/literacyscreening/${encodeURIComponent(leaId)}`,
  getFilesBySchool: (
    schoolYear: string,
    leaId: string,
    schoolIdentifier: string
  ): string =>
    `file/literacyscreening/${encodeURIComponent(
      schoolYear
    )}/${encodeURIComponent(leaId)}/${encodeURIComponent(schoolIdentifier)}`,
  downloadFile: (leaId: string, metaId: string): string =>
    `file/literacyscreening/download/${encodeURIComponent(
      leaId
    )}/${encodeURIComponent(metaId)}`,
};

export const LiteracyScreeningServiceClient = (
  config?: DataRequestConfig
): HttpClient =>
  getHttpClient(getApiBaseUrl(), getAuthToken() || '', config?.signal);

export const getLiteracyScreeningContact = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<LiteracyScreeningContactModel>> => {
  return executeApiRequest<LiteracyScreeningContactModel>(async () => {
    return await LiteracyScreeningServiceClient(config).get(
      literacyScreeningApiPaths.getContact
    );
  });
};

export const postLiteracyScreeningContact = async (
  contact: LiteracyScreeningContactModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<string>> => {
  return executeApiRequest<string>(async () => {
    return await LiteracyScreeningServiceClient(config).post(
      literacyScreeningApiPaths.createContact,
      contact
    );
  });
};

export const putLiteracyScreeningContact = async (
  contact: LiteracyScreeningContactModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<void>> => {
  return executeApiRequest<void>(async () => {
    return await LiteracyScreeningServiceClient(config).put(
      literacyScreeningApiPaths.updateContact,
      contact
    );
  });
};

export const getLiteracyScreeningSchoolYears = async (
  config?: DataRequestConfig
): Promise<ApiResponseModel<SelectOption[]>> => {
  return executeApiRequest<SelectOption[]>(async () => {
    return await LiteracyScreeningServiceClient(config).get(
      literacyScreeningApiPaths.getSchoolYears
    );
  });
};

export const getLiteracyScreeningLEASchools = async (
  schoolYear: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<LiteracyScreeningLEASchoolModel>> => {
  return executeApiRequest<LiteracyScreeningLEASchoolModel>(async () => {
    return await LiteracyScreeningServiceClient(config).get(
      literacyScreeningApiPaths.getSchoolsBySchoolYear(schoolYear)
    );
  });
};

export const getLiteracyScreeningDataCollectionChecks = async (
  schoolYear: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<boolean>> => {
  return executeApiRequest<boolean>(async () => {
    return await LiteracyScreeningServiceClient(config).get(
      literacyScreeningApiPaths.getDataCollectionChecks(schoolYear)
    );
  });
};

export const getLiteracyScreeningOptionSets = async (
  module: string,
  page: string,
  config?: DataRequestConfig,
  include?: string
): Promise<ApiResponseModel<OptionSetsPageModel>> => {
  return executeApiRequest<OptionSetsPageModel>(async () => {
    return await LiteracyScreeningServiceClient(config).get(
      literacyScreeningApiPaths.getOptionSets(module, page, include)
    );
  });
};

export const getLiteracyScreeningDiagnostics = async (
  schoolYear: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<LiteracyScreeningDiagnosticMeasuresModel[]>> => {
  return executeApiRequest<LiteracyScreeningDiagnosticMeasuresModel[]>(
    async () => {
      return await LiteracyScreeningServiceClient(config).get(
        literacyScreeningApiPaths.getDiagnostics(schoolYear)
      );
    }
  );
};

export const postLiteracyScreeningDiagnostics = async (
  model: LiteracyScreeningDiagnosticMeasuresModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<string>> => {
  return executeApiRequest<string>(async () => {
    return await LiteracyScreeningServiceClient(config).post(
      literacyScreeningApiPaths.createDiagnostics,
      model
    );
  });
};

export const putLiteracyScreeningDiagnostics = async (
  id: string,
  model: LiteracyScreeningDiagnosticMeasuresModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<void>> => {
  return executeApiRequest<void>(async () => {
    return await LiteracyScreeningServiceClient(config).put(
      literacyScreeningApiPaths.updateDiagnostics(id),
      model
    );
  });
};

export const postLiteracyScreening = async (
  model: LiteracyScreeningModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<string>> => {
  return executeApiRequest<string>(async () => {
    return await LiteracyScreeningServiceClient(config).post(
      literacyScreeningApiPaths.createScreening,
      model
    );
  });
};

export const putLiteracyScreening = async (
  id: string,
  model: LiteracyScreeningModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<void>> => {
  return executeApiRequest<void>(async () => {
    return await LiteracyScreeningServiceClient(config).put(
      literacyScreeningApiPaths.updateScreening(id),
      model
    );
  });
};

export const uploadLiteracyScreeningFile = async (
  schoolYear: string,
  schoolIdentifier: string,
  leaIdentifier: string,
  file: File,
  documentType: string,
  config?: DataRequestConfig & {
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  }
): Promise<ApiResponseModel<{ id: string }>> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('DocumentType', documentType);

  return executeApiRequest<{ id: string }>(async () => {
    return await LiteracyScreeningServiceClient(config).upload(
      literacyScreeningApiPaths.uploadFile(
        schoolYear,
        schoolIdentifier,
        leaIdentifier,
        documentType
      ),
      formData,
      config?.onUploadProgress
    );
  });
};

export const deleteLiteracyScreeningFile = async (
  leaId: string,
  id: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<void>> => {
  return executeApiRequest<void>(async () => {
    return await LiteracyScreeningServiceClient(config).delete(
      literacyScreeningApiPaths.deleteFile(leaId, id)
    );
  });
};

export const getLiteracyScreeningFileById = async (
  leaId: string,
  id: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<LiteracyScreeningFileListItemModel>> => {
  return executeApiRequest<LiteracyScreeningFileListItemModel>(async () => {
    return await LiteracyScreeningServiceClient(config).get(
      literacyScreeningApiPaths.getFileById(leaId, id)
    );
  });
};

export const getLiteracyScreeningFilesByLea = async (
  leaId: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<LiteracyScreeningFileListItemModel[]>> => {
  return executeApiRequest<LiteracyScreeningFileListItemModel[]>(async () => {
    return await LiteracyScreeningServiceClient(config).get(
      literacyScreeningApiPaths.getFilesByLea(leaId)
    );
  });
};

export const getLiteracyScreeningFilesBySchool = async (
  schoolYear: string,
  leaId: string,
  schoolIdentifier: string,
  config?: DataRequestConfig
): Promise<ApiResponseModel<LiteracyScreeningFileListItemModel[]>> => {
  return executeApiRequest<LiteracyScreeningFileListItemModel[]>(async () => {
    return await LiteracyScreeningServiceClient(config).get(
      literacyScreeningApiPaths.getFilesBySchool(
        schoolYear,
        leaId,
        schoolIdentifier
      )
    );
  });
};

export const downloadLiteracyScreeningFile = async (
  leaId: string,
  metaId: string,
  config?: DataRequestConfig
): Promise<Blob> => {
  const response = await LiteracyScreeningServiceClient(config).getFile<Blob>(
    literacyScreeningApiPaths.downloadFile(leaId, metaId)
  );
  return response.data;
};

export const putLiteracyScreeningDocument = async (
  id: string,
  model: LiteracyScreeningFileListItemModel,
  config?: DataRequestConfig
): Promise<ApiResponseModel<void>> => {
  return executeApiRequest<void>(async () => {
    return await LiteracyScreeningServiceClient(config).put(
      literacyScreeningApiPaths.updateDocument(id),
      {
        mdeStatus: model.mdeStatus,
        comments: model.comments,
      }
    );
  });
};
